import { brandOptions, organizations, contactPersons, adminUnits } from './itemsFixtureData';

/**
 * Deterministic fixture items for the Quantity-filter decimal-persistence epic (DL-63561 /
 * DL-58011, Testmo "dl-58011" folder): known fractional/zero `defaultQty` values so the
 * Between/To number-range filter has real boundary rows to prove against, independent of the
 * faker-derived `generateItem` pool (whose `defaultQty` is always an integer, `(i % 15) + 1`).
 * Isolable via `?search=QtyDecimalProbe` (unique substring, absent from every generated/other
 * fixture name) so a filter's result set is asserted against exactly these 7 rows, not the
 * 213-row generated pool. `applyFilters`' `defaultqty` branch (`Number(minRaw)`/`Number(maxRaw)`)
 * is decimal-precision-safe already — these fixtures exist to prove that in a spec, not to fix
 * the handler.
 *
 * Split out of `items.ts` — same reason `bulkUpdateProbeFixtureData.ts` was: keeps the handler
 * file under the repo's 800-line cap (`.eslintrc.js` `max-lines`). Base shape mirrors
 * `bulkEditProbeSupplierEmptyA` (`items.ts`) directly (not imported from it) to avoid a
 * circular import between the two handler files.
 */
const decimalQtyProbeBase = {
  description: null,
  brand: brandOptions[0],
  organization: organizations[0],
  supplier: null,
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  adminUnit: adminUnits[0],
  logo: null,
  isActive: true,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-01-15T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  customFields: [],
};

export const decimalQtyProbeZero = {
  ...decimalQtyProbeBase,
  id: 90400,
  name: 'QtyDecimalProbe Zero',
  userDefinedId: 'IT-90400',
  defaultQty: 0,
};

export const decimalQtyProbeOneAndHalf = {
  ...decimalQtyProbeBase,
  id: 90401,
  name: 'QtyDecimalProbe OneAndHalf',
  userDefinedId: 'IT-90401',
  defaultQty: 1.5,
};

export const decimalQtyProbeTwoAndHalf = {
  ...decimalQtyProbeBase,
  id: 90402,
  name: 'QtyDecimalProbe TwoAndHalf',
  userDefinedId: 'IT-90402',
  defaultQty: 2.5,
};

export const decimalQtyProbeFive = {
  ...decimalQtyProbeBase,
  id: 90403,
  name: 'QtyDecimalProbe Five',
  userDefinedId: 'IT-90403',
  defaultQty: 5,
};

export const decimalQtyProbeEightAndHalf = {
  ...decimalQtyProbeBase,
  id: 90404,
  name: 'QtyDecimalProbe EightAndHalf',
  userDefinedId: 'IT-90404',
  defaultQty: 8.5,
};

export const decimalQtyProbeNineSeventyFive = {
  ...decimalQtyProbeBase,
  id: 90405,
  name: 'QtyDecimalProbe NineSeventyFive',
  userDefinedId: 'IT-90405',
  defaultQty: 9.75,
};

export const decimalQtyProbeTen = {
  ...decimalQtyProbeBase,
  id: 90406,
  name: 'QtyDecimalProbe Ten',
  userDefinedId: 'IT-90406',
  defaultQty: 10,
};

/** Spread into `itemsData` as a single entry — mirrors `clearFieldItems`' export shape. */
export const decimalQtyProbeItems = [
  decimalQtyProbeZero,
  decimalQtyProbeOneAndHalf,
  decimalQtyProbeTwoAndHalf,
  decimalQtyProbeFive,
  decimalQtyProbeEightAndHalf,
  decimalQtyProbeNineSeventyFive,
  decimalQtyProbeTen,
];
